import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  Chip,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Restaurant as RestaurantIcon,
  QrCode2 as QrCode2Icon,
  ShoppingCart as ShoppingCartIcon,
  MenuBook as MenuBookIcon,
  Category as CategoryIcon,
  LocalOffer as LocalOfferIcon,
  People as PeopleIcon,
  Receipt as ReceiptIcon,
  PhoneIphone as PhoneIphoneIcon,
  WhatsApp as WhatsAppIcon,
  Payments as PaymentsIcon,
  DeliveryDining as DeliveryDiningIcon,
  RocketLaunch as RocketLaunchIcon,
  Timer as TimerIcon,
  Payment as PaymentIcon,
  Verified as VerifiedIcon,
  ArrowForward as ArrowForwardIcon,
  TrendingUp as TrendingUpIcon,
  Star as StarIcon,
  AttachMoney as MoneyIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PageBanner from "../components/PageBanner";
import PageTitle from "../components/PageTitle";
import pricingBanner from "../assets/images/pricing.avif";

const MotionBox = motion(Box);
const MotionPaper = motion(Paper);

const Pricing = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const features = [
    { icon: <QrCode2Icon />, label: "Unlimited QR Menu" },
    { icon: <ShoppingCartIcon />, label: "Unlimited Orders" },
    { icon: <MenuBookIcon />, label: "Menu Management" },
    { icon: <CategoryIcon />, label: "Category Management" },
    { icon: <LocalOfferIcon />, label: "Offers & Deals" },
    { icon: <PeopleIcon />, label: "Staff Management" },
    { icon: <ReceiptIcon />, label: "Billing System" },
    { icon: <PhoneIphoneIcon />, label: "Customer Web App" },
    { icon: <WhatsAppIcon />, label: "WhatsApp Notifications" },
    { icon: <PaymentsIcon />, label: "UPI & PhonePe Payments" },
    { icon: <DeliveryDiningIcon />, label: "Dine-in, Takeaway & Delivery" },
  ];

  const billingSteps = [
    {
      label: "Restaurant Registration",
      description: "Sign up and setup your restaurant profile",
      icon: <RestaurantIcon />,
      color: "var(--primary)",
      bgColor: "var(--bg-primary-soft)",
    },
    {
      label: "30 Days Free Usage",
      description: "Use all features absolutely free for 30 days",
      icon: <TimerIcon />,
      color: "var(--primary-dark)",
      bgColor: "rgba(255, 107, 0, 0.10)",
    },
    {
      label: "Invoice Generated",
      description: "Auto-generated invoice on day 30",
      icon: <ReceiptIcon />,
      color: "var(--primary)",
      bgColor: "var(--bg-orange)",
    },
    {
      label: "3 Days Grace Period",
      description: "3 additional days to make the payment",
      icon: <PaymentIcon />,
      color: "var(--primary-dark)",
      bgColor: "rgba(255, 107, 0, 0.12)",
    },
    {
      label: "₹299 Payment",
      description: "Pay ₹299 and continue using the service",
      icon: <VerifiedIcon />,
      color: "var(--primary)",
      bgColor: "var(--bg-primary-soft)",
    },
    {
      label: "Continue Using Service",
      description: "Uninterrupted service with all features",
      icon: <RocketLaunchIcon />,
      color: "var(--primary-dark)",
      bgColor: "rgba(255, 107, 0, 0.10)",
    },
  ];

  return (
    <>
    <PageTitle 
    title="RestaurantWala Pricing – Affordable Restaurant Management Software "
    description="Explore RestaurantWala pricing and get powerful tools for restaurant management, including QR menus, orders, billing, staff and reports."
    keywords="restaurant software pricing, restaurant management software pricing, restaurant POS pricing, affordable restaurant software"
    />
      <Navbar />
      <PageBanner
        title="Simple & Transparent Pricing"
        subtitle="One plan. Everything included. No hidden charges."
        chipLabel="PRICING"
        bgImage={pricingBanner}
      />

      <Box
        sx={{
          bgcolor: "var(--bg-light)",
          minHeight: "100vh",
          py: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          {/* Pricing Card */}
          <Grid container justifyContent="center">
            <Grid item size={{ xs: 12, md: 8, lg: 6 }}>
              <MotionPaper
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 4, md: 5 },
                  borderRadius: "30px",
                  bgcolor: "#ffffff",
                  border: "2px solid var(--primary)",
                  position: "relative",
                  overflow: "hidden",
                  textAlign: "center",
                  boxShadow: "var(--shadow-orange-hover)",
                }}
              >
                {/* Decorative Border */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background:
                      "linear-gradient(90deg, var(--primary), var(--primary-dark), var(--primary))",
                    backgroundSize: "200% 100%",
                    animation: "gradientMove 3s ease-in-out infinite",
                  }}
                />

                {/* Best Value Badge */}
                <Chip
                  label=" BEST VALUE"
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    bgcolor: "var(--primary)",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "10px",
                    letterSpacing: "1px",
                    borderRadius: "8px",
                  }}
                />

                {/* 30 Days Free Trial Badge */}
                <Chip
                  // icon={<TimerIcon sx={{ fontSize: 14 }} />}
                  label="30 Days Free Trial"
                  sx={{
                    position: "absolute",
                    top: 16,
                    left: 16,
                    // bgcolor: "var(--primary-dark)",
                     bgcolor: "var(--primary)",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "10px",
                    letterSpacing: "1px",
                    borderRadius: "8px",
                  }}
                />

                <Box sx={{ position: "relative", zIndex: 1, mt: 4 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: "3rem", sm: "4rem", md: "5rem" },
                      fontWeight: 900,
                      color: "var(--text)",
                      lineHeight: 1,
                      mt: 2,
                    }}
                  >
                    ₹299
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "1.2rem",
                        color: "var(--text-light)",
                        fontWeight: 600,
                        ml: 1,
                      }}
                    >
                      / Month
                    </Typography>
                  </Typography>

                  <Typography
                    sx={{
                      color: "var(--primary)",
                      fontWeight: 700,
                      fontSize: { xs: "14px", md: "16px" },
                      mt: 1,
                      mb: 3,
                    }}
                  >
                    One Plan. Everything Included.
                  </Typography>

                  <Box
                    sx={{
                      bgcolor: "var(--bg-primary-soft)",
                      borderRadius: "12px",
                      p: 2,
                      mb: 3,
                      border: "1px dashed var(--primary)",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "var(--primary)",
                        fontWeight: 700,
                        fontSize: { xs: "14px", md: "15px" },
                      }}
                    >
                      30 Days Free Trial • No Credit Card Required
                    </Typography>
                    <Typography
                      sx={{
                        color: "var(--text-light)",
                        fontSize: { xs: "12px", md: "13px" },
                        mt: 0.5,
                      }}
                    >
                      Start Free • Pay ₹299/month After 30 Days
                    </Typography>
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={1.5} sx={{ mb: 4 }}>
                    {features?.map((feature, index) => (
                      <Grid item size={{ xs: 12, sm: 6 }} key={index}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            p: 1,
                            borderRadius: "8px",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              bgcolor: "var(--bg-primary-soft)",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              color: "var(--primary)",
                              fontSize: { xs: 20, md: 22 },
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <CheckCircleIcon
                              sx={{ fontSize: { xs: 18, md: 20 } }}
                            />
                          </Box>
                          <Typography
                            sx={{
                              fontSize: { xs: "13px", md: "14px" },
                              color: "var(--text)",
                              fontWeight: 500,
                            }}
                          >
                            {feature.label}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  <Button
                    variant="contained"
                    size="large"
                    component={Link}
                    to="/register"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      bgcolor: "var(--primary)",
                      color: "#fff",
                      px: { xs: 4, md: 6 },
                      py: { xs: 1.2, md: 1.5 },
                      borderRadius: "14px",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: { xs: "1rem", md: "1.1rem" },
                      boxShadow: "var(--shadow-orange)",
                      width: "100%",
                      "&:hover": {
                        bgcolor: "var(--primary-dark)",
                        transform: "translateY(-3px)",
                        boxShadow: "var(--shadow-orange-hover)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Start Free for 30 Days
                  </Button>

                  <Typography
                    sx={{
                      color: "var(--text-light)",
                      fontSize: { xs: "12px", md: "13px" },
                      mt: 2,
                    }}
                  >
                    No credit card required. Cancel anytime.
                  </Typography>
                </Box>
              </MotionPaper>
            </Grid>
          </Grid>
          {/* FAQ / Additional Info */}
          <Box
            sx={{
              mt: { xs: 4, md: 5 },
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: { xs: 2, md: 3 },
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: "16px",
                bgcolor: "#ffffff",
                border: "1px solid var(--border)",
                textAlign: "center",
              }}
            >
              <Typography sx={{ fontSize: "2rem", mb: 1 }}>❓</Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "var(--text)",
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  mb: 0.5,
                }}
              >
                Have Questions?
              </Typography>
              <Typography
                sx={{ color: "var(--text-light)", fontSize: "14px", mb: 2 }}
              >
                We're here to help you
              </Typography>
              <Button
                variant="outlined"
                component={Link}
                to="/contact"
                sx={{
                  color: "var(--primary)",
                  borderColor: "var(--primary)",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "var(--primary-dark)",
                    bgcolor: "var(--bg-primary-soft)",
                  },
                }}
              >
                Contact Us
              </Button>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: "16px",
                bgcolor: "#ffffff",
                border: "1px solid var(--border)",
                textAlign: "center",
              }}
            >
              <Typography sx={{ fontSize: "2rem", mb: 1 }}>💬</Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "var(--text)",
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  mb: 0.5,
                }}
              >
                Quick Support
              </Typography>
              <Typography
                sx={{ color: "var(--text-light)", fontSize: "14px", mb: 2 }}
              >
                Email: restaurantwala24@gmail.com
              </Typography>
              <Button
                variant="contained"
                startIcon={<WhatsAppIcon />}
                sx={{
                  bgcolor: "#25D366",
                  color: "#fff",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    bgcolor: "#1da851",
                  },
                }}
                 href="https://wa.me/916378788756?text=Hello RestaurantWala%20Team%2C%20I%20would%20like%20to%20inquire%20about%20your%20services."
                 target="_blank"
              >
                WhatsApp Us
              </Button>
            </Paper>
          </Box>
        </Container>
      </Box>

      <Footer />

      {/* Animation Keyframes */}
      <style>
        {`
          @keyframes gradientMove {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}
      </style>
    </>
  );
};

export default Pricing;
